import { supabase } from '@/integrations/supabase/client';
import { Message } from '@/types';

// React import (precisa estar no topo, mas aqui para evitar erro de ordem)
import React from 'react';

export interface StreamingToken { 
  content: string;
  isComplete: boolean;
  metadata?: any;
}

export interface StreamingResponse {
  [Symbol.asyncIterator](): AsyncIterableIterator<StreamingToken>;
  close: () => void;
}

/**
 * ⚡ STREAMING SERVICE - Consumo token-by-token real
 * Substitui await response.json() por stream real
 */
export async function fetchStreamingResponse({
  payload,
  signal
}: {
  payload: any;
  signal?: AbortSignal;
}): Promise<StreamingResponse> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  
  if (!accessToken) {
    throw new Error('Token de acesso não disponível');
  }

  console.log('🚀 Iniciando stream real com OpenAI...');
  
  const SUPABASE_URL = 'https://jfdgdjxvzsmuwofclqyt.supabase.co';
  const response = await fetch(`${SUPABASE_URL}/functions/v1/openai-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'text/event-stream'
    },
    body: JSON.stringify(payload),
    signal
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Erro no streaming: ${errorData.error || response.statusText}`);
  }

  if (!response.body) {
    throw new Error('Stream não disponível');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let isClosed = false;

  const close = () => {
    isClosed = true;
    reader.cancel();
  };

  const asyncIterator = {
    async *[Symbol.asyncIterator](): AsyncIterableIterator<StreamingToken> {
      try {
        while (!isClosed) {
          const { value, done } = await reader.read();
          
          if (done) {
            console.log('✅ Stream concluído');
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Manter linha incompleta no buffer

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line || !line.startsWith('data:')) continue;
            
            const data = line.slice(5).trim(); // Remove "data: "
            
            if (data === '[DONE]') {
              console.log('🏁 Stream finalizado pelo servidor');
              return { content: '', isComplete: true };
            }

            try {
              const json = JSON.parse(data);
              
              // Extrair token do formato OpenAI ou formato customizado
              const token = json?.choices?.[0]?.delta?.content || json?.delta || '';
              
              if (token) {
                yield {
                  content: token,
                  isComplete: false,
                  metadata: json.metadata
                };
              }
              
              // Check if stream is complete
              if (json?.choices?.[0]?.finish_reason || json?.isComplete) {
                console.log('✅ Stream finalizado:', json.choices?.[0]?.finish_reason || 'stream_complete');
                yield {
                  content: '',
                  isComplete: true,
                  metadata: json.metadata
                };
                return;
              }
              
            } catch (parseError) {
              console.warn('⚠️ Erro ao parsear chunk SSE:', parseError, 'Data:', data);
              // Continuar mesmo com erro de parsing
            }
          }
        }
      } catch (error) {
        console.error('❌ Erro no stream:', error);
        throw error;
      }
    }
  };

  return {
    [Symbol.asyncIterator]: asyncIterator[Symbol.asyncIterator],
    close
  };
}

/**
 * Hook React para usar streaming em componentes
 */
export function useStreamingMessage() {
  const [streamedContent, setStreamedContent] = React.useState('');
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const startStreaming = async (
    payload: any,
    signal?: AbortSignal
  ) => {
    setStreamedContent('');
    setIsStreaming(true);
    setError(null);
    
    try {
      const stream = await fetchStreamingResponse({ payload, signal });
      
      for await (const token of stream) {
        if (token.isComplete) {
          setIsStreaming(false);
          break;
        }
        
        setStreamedContent(prev => prev + token.content);
      }
    } catch (err) {
      console.error('❌ Erro no streaming:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      setIsStreaming(false);
    }
  };

  return {
    streamedContent,
    isStreaming,
    error,
    startStreaming
  };
}